/**
 * Multidev Collaboration Hub SDK Entry Point
 * Initializes the commenting overlay on Multidev environments
 *
 * Uses Shadow DOM for style isolation from host page.
 * Injects PDS design tokens for consistent Pantheon branding.
 */

import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { MultidevCollaborationApp } from './MultidevCollaborationApp';
import { pdsTokensCSS } from './styles/tokens';

interface SDKConfig {
  siteId: string;
  environmentId: string;
  apiUrl: string;
}

class MultidevCollaborationSDK {
  private root: Root | null = null;
  private hostElement: HTMLDivElement | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private config: SDKConfig | null = null;

  /**
   * Initialize the SDK from script tag data attributes
   */
  public init(): void {
    try {
      // Find the SDK script tag
      const scriptTag = document.currentScript as HTMLScriptElement | null;
      if (!scriptTag) {
        console.error('[Multidev Collaboration] Could not find SDK script tag');
        return;
      }

      // Extract configuration from data attributes
      const siteId = scriptTag.dataset.siteId;
      const environmentId = scriptTag.dataset.environment;
      const apiUrl = scriptTag.dataset.apiUrl;

      if (!siteId || !environmentId || !apiUrl) {
        console.error('[Multidev Collaboration] Missing required data attributes on script tag');
        return;
      }

      this.config = { siteId, environmentId, apiUrl };

      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.mount());
      } else {
        this.mount();
      }
    } catch (error) {
      console.error('[Multidev Collaboration] Initialization error:', error);
    }
  }

  /**
   * Mount the React application inside Shadow DOM
   */
  private mount(): void {
    if (!this.config) {
      console.error('[Multidev Collaboration] Cannot mount: missing configuration');
      return;
    }

    try {
      // Create host element for Shadow DOM
      this.hostElement = document.createElement('div');
      this.hostElement.id = 'multidev-collaboration-sdk-host';
      this.hostElement.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 999999;
      `;

      // Append host to body
      document.body.appendChild(this.hostElement);

      // Create Shadow DOM for style isolation
      this.shadowRoot = this.hostElement.attachShadow({ mode: 'open' });

      // Inject PDS design tokens into shadow root
      const styleElement = document.createElement('style');
      styleElement.textContent = pdsTokensCSS;
      this.shadowRoot.appendChild(styleElement);

      // Create container for React inside shadow root
      const container = document.createElement('div');
      container.id = 'multidev-collaboration-sdk-root';
      container.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
      `;
      this.shadowRoot.appendChild(container);

      // Create React root and render
      this.root = createRoot(container);
      this.root.render(
        <React.StrictMode>
          <MultidevCollaborationApp config={this.config} />
        </React.StrictMode>
      );

      if (process.env.NODE_ENV !== 'production') {
        console.log('[Multidev Collaboration] SDK initialized successfully with Shadow DOM');
      }
    } catch (error) {
      console.error('[Multidev Collaboration] Mount error:', error);
    }
  }

  /**
   * Unmount and cleanup
   */
  public destroy(): void {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }

    if (this.hostElement && this.hostElement.parentNode) {
      this.hostElement.parentNode.removeChild(this.hostElement);
      this.hostElement = null;
    }

    this.shadowRoot = null;
    this.config = null;
    if (process.env.NODE_ENV !== 'production') {
      console.log('[Multidev Collaboration] SDK destroyed');
    }
  }
}

// Auto-initialize when script loads
const sdk = new MultidevCollaborationSDK();
sdk.init();

// Expose SDK instance globally for programmatic control
(window as any).MultidevCollaborationSDK = sdk;

// Export SDK
export { MultidevCollaborationSDK };
export type { SDKConfig };

// Export Auth components and utilities
export { AuthManager } from './lib/auth-manager';
export type { AuthConfig, UserProfile } from './lib/auth-manager';
export { SignInPrompt } from './components/auth/SignInPrompt';
export type { SignInPromptProps } from './components/auth/SignInPrompt';
export { AuthManagerProvider, useAuthManager } from './context/AuthManagerContext';
export type { AuthManagerProviderProps } from './context/AuthManagerContext';
